// @ts-strict-ignore
import {Component, EventEmitter, Input, Output} from "@angular/core";
import {AudioChannel, AudioDeviceMode, Media, MediaType, PlayerMapping, ActivityState} from "@models";
import {SimConfigurationService, SimMaterialDialogService, SimUtilityExtendedService} from "@services";
import {Translations} from "@translations";
import {MatChipSelectionChange} from "@angular/material/chips";

@Component({
	selector: "sim-media-card-list",
	templateUrl: "./media-card-list.component.html",
	styleUrls: ["./media-card-list.component.scss"],
	standalone: false,
})
export class MediaCardListComponent {
	// Input
	@Input() active: boolean;
	@Input() index: number;
	@Input() presentationControls: boolean;
	@Input() usedWithinPresentationCommander: boolean = false;
	@Input() previewMedia: boolean;
	@Input() previewMediaType: MediaType;
	@Input() selectedAudioChannelIds: string[];
	@Input() media: Media;
	@Input() showDeleteButton: boolean;
	@Input() showAddButton: boolean;
	@Input() showEditButton: boolean;
	@Input() showDownloadButton: boolean;
	@Input() currentAudioChannels: AudioChannel[];
	@Input() showPreview: boolean = true;
	@Input() showPreviewButton: boolean = false;
	@Input() progress: number;
	@Input() mediaName: string;
	@Input() mediaInEditMode: boolean;
	@Input() isUploading: boolean;
	@Input() uploadProgress: number;
	@Input() previewMediaUrl: string;
	@Input() errorMessage: string;
	@Input() pagePos: number = 1;
	@Input() currentSelectedFile: File;
	@Input() showFileTypeIcon: boolean = true;
	@Input() isDraggable: boolean = false;
	@Input() showLoopButton: boolean = false;
	@Input() isLooping: boolean = false;
	// used to display the remove icon instead of the trash delete icon
	@Input() useRemoveIcon: boolean = false;
	@Input() mediaPlayer: PlayerMapping;

	// Output
	@Output() mediaClicked: EventEmitter<any> = new EventEmitter<any>();
	@Output() toggleVogMediaLooping: EventEmitter<Media> = new EventEmitter<Media>();
	@Output() toggleAudioChannel: EventEmitter<{
		media: Media;
		audioChannel: AudioChannel;
	}> = new EventEmitter<{media: Media; audioChannel: AudioChannel}>();
	@Output() deleteButtonClicked: EventEmitter<void> = new EventEmitter<void>();
	@Output() addButtonClicked: EventEmitter<void> = new EventEmitter<void>();
	@Output() editButtonClicked: EventEmitter<void> = new EventEmitter<void>();
	@Output() stopMedia: EventEmitter<any> = new EventEmitter<any>();
	@Output() backwardClicked: EventEmitter<any> = new EventEmitter<any>();
	@Output() forwardClicked: EventEmitter<any> = new EventEmitter<any>();
	@Output() mediaNameChange: EventEmitter<string> = new EventEmitter<string>();

	// Properties
	T = Translations;
	MediaType = MediaType;
	pdfLoadingError: boolean = false;
	pdfLoaded: boolean = false;
	pptxLoadingError: boolean;
	powerPointLoaded: boolean;
	settings = SimConfigurationService.settings;
	AudioDeviceMode = AudioDeviceMode;
	ActivityState = ActivityState;

	public get getProgress() {
		if (isNaN(this.progress)) return 0;
		return this.progress * 100;
	}

	constructor(
		public simUtilityService: SimUtilityExtendedService,
		private _simMaterialDialogService: SimMaterialDialogService,
	) {}

	openImageInDialog() {
		if (!this.previewMedia) {
			this._simMaterialDialogService
				.openImageDialog({
					type: "media",
					media: this.media,
				})
				.then();
		} else {
			this._simMaterialDialogService
				.openImageDialog({
					type: "url",
					mediaUrl: this.previewMediaUrl,
					mediaName: this.mediaName,
				})
				.then();
		}
	}

	mediaWasClicked() {
		switch (this.previewMedia ? this.previewMediaType : this.media.mediaType) {
			case MediaType.Image:
				this.openImageInDialog();
				break;
			case MediaType.Video:
				this.openVideoDialog();
				break;
			case MediaType.Audio:
				this.mediaClicked.emit();
				break;
			case MediaType.PDF:
				this.openPDFInDialog();
				break;
			case MediaType.PowerPoint:
				this.openPowerPointDialog();
				break;
			default:
				this.mediaClicked.emit();
		}
	}

	downloadMedia() {
		const mediaDownloadLink = document.createElement("a");
		mediaDownloadLink.href = this.simUtilityService.getMediaPath(this.media);
		mediaDownloadLink.download = this.media.name;
		document.body.appendChild(mediaDownloadLink);
		mediaDownloadLink.click();
		document.body.removeChild(mediaDownloadLink);
	}

	openVideoDialog() {
		if (!this.previewMedia) {
			this._simMaterialDialogService.openVideoDialog({media: this.media});
		} else {
			this._simMaterialDialogService.openVideoDialog({
				mediaUrl: this.previewMediaUrl,
				mediaName: this.mediaName,
			});
		}
	}

	mediaNameChanged() {
		this.mediaInEditMode = !this.mediaInEditMode;
		this.mediaNameChange.emit(this.mediaName);
	}

	openPDFInDialog() {
		if (!this.previewMedia) {
			this._simMaterialDialogService.openPDFDialog({media: this.media});
		} else {
			this._simMaterialDialogService.openPDFDialog({
				mediaUrl: this.previewMediaUrl,
				mediaName: this.mediaName,
			});
		}
	}

	openPowerPointDialog() {
		if (!this.previewMedia) {
			this._simMaterialDialogService.openPowerPointDialog({media: this.media}).then();
		} else {
			this._simMaterialDialogService
				.openPowerPointDialog({
					mediaUrl: this.currentSelectedFile ? this.currentSelectedFile : this.previewMediaUrl,
					mediaName: this.mediaName,
				})
				.then();
		}
	}

	handleAudioChannelSelection(media: Media, audioChannel: AudioChannel, $event: MatChipSelectionChange) {
		if ($event.isUserInput) {
			this.toggleAudioChannel.emit({
				media: media,
				audioChannel: audioChannel,
			});
		}
	}
}
